import { ArrowLeft, Check, Handshake, Package, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { ParceiroDaVitrineResponse, ProdutoDoParceiroResponse } from '@/api/generated/model';
import { ParceiroDaVitrineResponseCategoria } from '@/api/generated/model';
import {
  useParceirosControllerListar,
  useParceirosControllerObter,
} from '@/api/generated/parceiros/parceiros';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';

/** Quanto tempo o item fica marcado como "Adicionado!" depois do clique. */
const MS_CONFIRMACAO = 1200;

/**
 * Laboratório não entra na seleção de produtos do receituário.
 *
 * A vitrine (`GET /v1/parceiros`) devolve todo parceiro publicado, inclusive os
 * de categoria `Laboratório` — que existem ali para exame, não para prescrição.
 * O filtro é no cliente porque a rota só aceita `categoria` como inclusão
 * (uma categoria por vez), não exclusão.
 */
const CATEGORIA_OCULTA = ParceiroDaVitrineResponseCategoria.Laboratório;

/**
 * O produto do parceiro traz só o nome (vira a descrição do item).
 *
 * `posologia` e `quantidade` entram vazias de propósito: o parceiro não envia
 * essas informações — o manual pede dele apenas nome, categoria, descrição e a
 * imagem 1:1 — e prescrever dose é ato do veterinário, não do fabricante.
 */
export interface ProdutoImportado {
  produtoParceiroId: string;
  descricao: string;
  posologia: string | null;
  quantidade: string | null;
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  aoImportar: (produto: ProdutoImportado) => void;
}

/**
 * Importa um produto de parceiro para dentro do receituário.
 *
 * Mesmo fluxo da V1 (`ProdutosParceirosModal.tsx`): grade de **cards de
 * parceiro** com o logo → lista de produtos daquele parceiro, cada um com a
 * sua imagem. A imagem não é enfeite: o manual pede do parceiro um quadrado
 * 800×800 justamente para o "menu de seleção que o veterinário usa ao montar
 * uma receita" (`docs-migração/parceiros-especificacoes-imagem.md`).
 *
 * ⚠️ **O caminho é por PARCEIRO, não por uma lista global de produtos.** Aqui
 * não existe rota de produtos para o médico — `/v1/admin/produtos-parceiros`
 * exige `admin_produtos_parceiros.*`, que o cargo `medico` não tem. O que o
 * médico alcança é `GET /v1/parceiros` e o detalhe, que traz `produtos`.
 *
 * O item importado guarda `produtoParceiroId` — é ele que alimenta a métrica de
 * importação do parceiro.
 */
export function ImportarProdutoDialogo({ aberto, aoFechar, aoImportar }: Props) {
  const [parceiro, setParceiro] = useState<ParceiroDaVitrineResponse | null>(null);
  const [busca, setBusca] = useState('');
  const [recemImportado, setRecemImportado] = useState<string | null>(null);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: vitrine,
    isPending: carregandoParceiros,
    isError: erroParceiros,
    refetch: recarregarParceiros,
  } = useParceirosControllerListar(undefined, { query: { enabled: aberto } });

  const {
    data: detalhe,
    isPending: carregandoProdutos,
    isError: erroProdutos,
    refetch: recarregarProdutos,
  } = useParceirosControllerObter(parceiro?.id ?? '', {
    query: { enabled: aberto && Boolean(parceiro) },
  });

  // Fechou, volta para a grade: a próxima importação pode ser de outro
  // parceiro, e reabrir no meio do caminho anterior esconde a escolha.
  useEffect(() => {
    if (aberto) return;
    setParceiro(null);
    setBusca('');
    setRecemImportado(null);
  }, [aberto]);

  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    [],
  );

  function escolherParceiro(escolhido: ParceiroDaVitrineResponse) {
    setParceiro(escolhido);
    setBusca('');
  }

  function voltar() {
    setParceiro(null);
    setBusca('');
  }

  function importar(produto: ProdutoDoParceiroResponse) {
    aoImportar({
      produtoParceiroId: produto.id,
      descricao: produto.nome,
      posologia: null,
      quantidade: null,
    });

    // O diálogo NÃO fecha, como na V1: receituário costuma levar mais de um
    // produto do mesmo parceiro, e reabrir + reescolher o parceiro a cada item
    // seria trabalho repetido. O aviso fica no próprio botão.
    setRecemImportado(produto.id);
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => setRecemImportado(null), MS_CONFIRMACAO);

    toast.success(`"${produto.nome}" adicionado ao receituário.`);
  }

  const parceiros = (vitrine?.itens ?? []).filter((p) => p.categoria !== CATEGORIA_OCULTA);

  const termo = busca.trim().toLowerCase();
  const produtos = (detalhe?.produtos ?? []).filter((p) =>
    [p.nome, p.categoria, p.descricao].some((campo) => campo?.toLowerCase().includes(termo)),
  );

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {parceiro ? `Produtos — ${parceiro.nome}` : 'Adicionar produto de parceiro'}
          </DialogTitle>
          <DialogDescription>
            {parceiro
              ? 'O nome do produto entra como descrição do item. Posologia e quantidade você preenche.'
              : 'Selecione um parceiro para acessar os produtos disponíveis para prescrição.'}
          </DialogDescription>
        </DialogHeader>

        {!parceiro ? (
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {carregandoParceiros ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <Skeleton key={i} className="h-44 w-full rounded-xl" />
                ))}
              </div>
            ) : erroParceiros ? (
              <EstadoErro aoTentarDeNovo={() => void recarregarParceiros()} />
            ) : parceiros.length === 0 ? (
              <EstadoVazio
                icone={Handshake}
                titulo="Nenhum parceiro na vitrine"
                descricao="Sem parceiro publicado não há produto para importar."
                className="py-10"
              />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {parceiros.map((item) => (
                  <CardDoParceiro
                    key={item.id}
                    parceiro={item}
                    aoEscolher={() => escolherParceiro(item)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={voltar}
                aria-label="Voltar aos parceiros"
                className="shrink-0 text-muted-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar produto..."
                aria-label="Buscar produto"
              />
            </div>

            <div className="max-h-[55vh] overflow-y-auto pr-1">
              {carregandoProdutos ? (
                <div className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-24 w-full rounded-lg" />
                  ))}
                </div>
              ) : erroProdutos ? (
                <EstadoErro aoTentarDeNovo={() => void recarregarProdutos()} />
              ) : produtos.length === 0 ? (
                <EstadoVazio
                  icone={Package}
                  titulo={termo ? 'Nenhum produto encontrado' : 'Este parceiro não tem produtos'}
                  descricao={termo ? 'Tente outro termo.' : 'Volte e escolha outro parceiro.'}
                  className="py-10"
                />
              ) : (
                <ul className="space-y-2">
                  {produtos.map((produto) => (
                    <LinhaDeProduto
                      key={produto.id}
                      produto={produto}
                      importado={recemImportado === produto.id}
                      aoImportar={() => importar(produto)}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CardDoParceiro({
  parceiro,
  aoEscolher,
}: {
  parceiro: ParceiroDaVitrineResponse;
  aoEscolher: () => void;
}) {
  return (
    <button
      type="button"
      onClick={aoEscolher}
      className="flex flex-col items-center text-center p-3 rounded-xl border border-border hover:border-brand/40 hover:bg-brand/5 transition-colors"
    >
      {/* Fundo branco: logo de parceiro costuma vir com fundo transparente e
          sumiria contra o card no tema escuro. */}
      <div className="w-full aspect-[3/1] rounded-lg bg-white flex items-center justify-center mb-3">
        {parceiro.logoUrl ? (
          // Sempre o logo, sem padding: o quadro e o logo compartilham a
          // proporção 3:1 exigida pelo servidor, então ele preenche exato. A
          // capa tem proporção livre e cada card saía com um recorte diferente.
          <img
            src={parceiro.logoUrl}
            alt={parceiro.nome}
            className="w-full h-full object-contain"
          />
        ) : (
          <Handshake className="w-8 h-8 text-muted-foreground/40" />
        )}
      </div>

      <span className="w-full truncate text-sm font-medium text-foreground">{parceiro.nome}</span>
      {parceiro.categoria && (
        <Badge variant="secondary" className="mt-1 text-[10px]">
          {parceiro.categoria}
        </Badge>
      )}
    </button>
  );
}

function LinhaDeProduto({
  produto,
  importado,
  aoImportar,
}: {
  produto: ProdutoDoParceiroResponse;
  importado: boolean;
  aoImportar: () => void;
}) {
  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 transition-colors',
        importado ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-border',
      )}
    >
      {produto.imagemUrl ? (
        <img
          src={produto.imagemUrl}
          alt={produto.nome}
          className="w-20 h-20 shrink-0 rounded-lg object-contain border border-border bg-white"
        />
      ) : (
        <div className="w-20 h-20 shrink-0 rounded-lg bg-brand/10 flex items-center justify-center">
          <Package className="w-7 h-7 text-brand/50" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">{produto.nome}</p>
          {produto.categoria && (
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {produto.categoria}
            </Badge>
          )}
        </div>
        {produto.descricao && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{produto.descricao}</p>
        )}
      </div>

      <Button
        type="button"
        size="sm"
        disabled={importado}
        onClick={aoImportar}
        aria-label={`Importar ${produto.nome}`}
        className={cn(
          'shrink-0 gap-1',
          importado && 'bg-emerald-600 hover:bg-emerald-600 text-white disabled:opacity-100',
        )}
      >
        {importado ? (
          <>
            <Check className="w-3 h-3" />
            Adicionado!
          </>
        ) : (
          <>
            <Plus className="w-3 h-3" />
            Adicionar
          </>
        )}
      </Button>
    </li>
  );
}
